import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  CalendarIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import { toastError, toastSuccess } from "../utils/toast";
import {
  isValidPublicationQuantity,
  normalizePublicationQuantityInput,
  parsePublicationQuantity,
} from "../utils/publicationQuantity";

const MAX_REPORT_WINDOW_DAYS = 30;

const startOfMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

const formatMonthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const formatDateLabel = (value) => {
  if (!value) return "Data non disponibile";
  let date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    date = new Date(`${value}T00:00:00`);
  }
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatMonthLabel = (date) =>
  date.toLocaleDateString("it-IT", { month: "long", year: "numeric" });

const formatDuration = (totalMinutes) => {
  const ore = Math.floor(totalMinutes / 60);
  const minuti = totalMinutes % 60;
  return `${ore}h ${minuti}m`;
};

const toDateOnly = (value) => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  date.setHours(0, 0, 0, 0);
  return date;
};

const isPostazioneAvailableOnDate = (postazione, dateObj) => {
  if (!postazione) {
    return false;
  }
  if (!dateObj) {
    return true;
  }
  if (!postazione.temporanea) {
    return true;
  }

  const from = toDateOnly(postazione.valid_from);
  const to = toDateOnly(postazione.valid_to);
  if (!from || !to) {
    return false;
  }
  return dateObj.getTime() >= from.getTime() && dateObj.getTime() <= to.getTime();
};

const parsePublications = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }
  return [];
};

const createInitialFormState = () => {
  const todayStr = new Date().toISOString().slice(0, 10);
  return {
    tipo: "postazione",
    data_servizio: todayStr,
    postazione_id: "",
    slot_orario_id: "",
    ore: 1,
    minuti: 0,
    ore_ldc: 0,
    studi: 0,
    pubblicazioni: [],
    note: "",
    esperienza_id: null,
  };
};

const PUBLICATION_LANGUAGES = [
  "Arabo",
  "Bengali",
  "Cinese",
  "Creolo",
  "Francese",
  "Giapponese",
  "Hindi",
  "Indonesiano",
  "Inglese",
  "Italiano",
  "Malagasy",
  "Malayalam",
  "Olandese",
  "Portoghese",
  "Punjabi",
  "Spagnolo",
  "Romeno",
  "Russo",
  "Tagalog",
  "Tamil",
  "Tedesco",
  "Ucraino",
  "Altro",
];

const PUBLICATION_TITLES = [
  "Bibbia",
  "Opuscolo Famiglia",
  "Riviste",
  "BHS",
  "Ricerca",
  "Op. 10 Domande",
  "Contatti",
  "Video",
  "Studi",
  "Op. Puoi vivere felice",
  "Libro Puoi vivere felice",
  "Op. Creazione",
  "Volantini",
  "Biglietti da Visita",
  "Altro",
];

const ALLOWED_MODES = ["both", "personal", "admin"];

const RapportiServizioSection = ({ mode = "both", showHeader = true }) => {
  const { user, activeCongregazione } = useAuth();
  const rapportoEnabled = Boolean(
    activeCongregazione?.rapporto_servizio_attivo ??
      user?.congregazione_rapporto_servizio_attivo
  );
  const isAdmin =
    user?.ruolo === "admin" || user?.ruolo === "super_admin";
  const isServizioSpeciale = Boolean(activeCongregazione?.servizio_speciale);
  const normalizedMode = ALLOWED_MODES.includes(mode) ? mode : "both";
  const allowPersonalScope = normalizedMode !== "admin";
  const allowAdminScope = isAdmin && normalizedMode !== "personal";

  const initialMonth = startOfMonth(new Date());
  const [activeTab, setActiveTab] = useState(
    allowPersonalScope ? "miei" : "admin"
  );
  const [monthState, setMonthState] = useState({
    miei: initialMonth,
    admin: initialMonth,
  });
  const [reportCache, setReportCache] = useState({
    miei: {},
    admin: {},
  });
  const reportCacheRef = useRef(reportCache);
  const [adminFilters, setAdminFilters] = useState({
    tipo: "all", // all | postazione_all | normale | postazione_{id}
    esperienza: "all",
    search: "",
    group: "all",
  });
  const [modalState, setModalState] = useState({
    open: false,
    scope: "miei",
    report: null,
  });
  const [formData, setFormData] = useState(createInitialFormState);
  const [experienceEnabled, setExperienceEnabled] = useState(false);
  const [experienceNote, setExperienceNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [postazioni, setPostazioni] = useState([]);
  const [postazioniLoading, setPostazioniLoading] = useState(false);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [volontari, setVolontari] = useState([]);
  const [supportingDataLoading, setSupportingDataLoading] = useState(false);
  const [adminPostazioni, setAdminPostazioni] = useState([]);
  const [openPublicationSummaryId, setOpenPublicationSummaryId] = useState(null);
  const adminSummaryRef = useRef(null);
  const publicationQuantityInputRefs = useRef([]);

  useEffect(() => {
    setActiveTab((prev) => {
      if (!allowPersonalScope && prev === "miei" && allowAdminScope) {
        return "admin";
      }
      if (!allowAdminScope && prev === "admin" && allowPersonalScope) {
        return "miei";
      }
      return prev;
    });
  }, [allowPersonalScope, allowAdminScope]);

  // Reset cache quando cambia la congregazione attiva per evitare dati stantii
  useEffect(() => {
    setReportCache({ miei: {}, admin: {} });
    reportCacheRef.current = { miei: {}, admin: {} };
  }, [activeCongregazione?.id]);

  // Chiudi il tooltip delle pubblicazioni quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openPublicationSummaryId &&
        adminSummaryRef.current &&
        !adminSummaryRef.current.contains(event.target)
      ) {
        setOpenPublicationSummaryId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openPublicationSummaryId]);

  // Chiudi tooltip quando cambia tab o mese
  useEffect(() => {
    setOpenPublicationSummaryId(null);
  }, [activeTab, monthState]);

  const reportEntries = useMemo(() => {
    const key = formatMonthKey(monthState[activeTab]);
    const cache = reportCache[activeTab]?.[key];
    return {
      loading: cache?.loading || false,
      error: cache?.error || null,
      items: cache?.items || [],
      range: cache?.range || null,
    };
  }, [reportCache, activeTab, monthState]);

  const rangeStart = reportEntries.range?.start;

  const selectedServiceDate = useMemo(() => {
    const explicitDate = toDateOnly(formData.data_servizio);
    if (explicitDate) {
      return explicitDate;
    }
    return toDateOnly(rangeStart);
  }, [formData.data_servizio, rangeStart]);

  const visiblePostazioni = useMemo(() => {
    if (!selectedServiceDate) {
      return postazioni;
    }
    return postazioni.filter((postazione) =>
      isPostazioneAvailableOnDate(postazione, selectedServiceDate)
    );
  }, [postazioni, selectedServiceDate]);

  useEffect(() => {
    if (!formData.postazione_id) {
      return;
    }
    const isOptionVisible = visiblePostazioni.some(
      (postazione) => String(postazione.id) === String(formData.postazione_id)
    );
    if (!isOptionVisible) {
      setFormData((prev) => ({
        ...prev,
        postazione_id: "",
        slot_orario_id: "",
      }));
    }
  }, [formData.postazione_id, visiblePostazioni]);

  const volunteersMap = useMemo(() => {
    const map = new Map();
    volontari.forEach((vol) => {
      if (vol?.id != null) {
        map.set(String(vol.id), vol);
      }
    });
    return map;
  }, [volontari]);

  const groupsMap = useMemo(() => {
    const map = new Map();
    serviceGroups.forEach((group) => {
      if (group?.id != null) {
        map.set(String(group.id), group);
      }
    });
    return map;
  }, [serviceGroups]);

  const filteredItems = useMemo(() => {
    if (activeTab === "admin") {
      const search = adminFilters.search.trim().toLowerCase();
      return reportEntries.items.filter((item) => {
        // Tipo / Postazione
        if (adminFilters.tipo === "normale") {
          if (item.tipo !== "normale") return false;
        } else if (adminFilters.tipo === "postazione_all") {
          if (item.tipo !== "postazione") return false;
        } else if (adminFilters.tipo.startsWith("postazione_")) {
          if (item.tipo !== "postazione") return false;
          const targetId = adminFilters.tipo.replace("postazione_", "");
          const repPostId =
            item.postazione_id != null ? String(item.postazione_id) : null;
          if (repPostId !== targetId) return false;
        }

        if (adminFilters.esperienza === "with" && !item.esperienza_id) {
          return false;
        }
        if (adminFilters.esperienza === "without" && item.esperienza_id) {
          return false;
        }
        const fullName = `${item.volontario_nome || ""} ${
          item.volontario_cognome || ""
        }`.toLowerCase();
        if (search && !fullName.includes(search)) {
          return false;
        }
        const volunteer =
          item.volontario_id != null
            ? volunteersMap.get(String(item.volontario_id))
            : null;
        const groupId = volunteer?.group_id
          ? String(volunteer.group_id)
          : null;
        if (adminFilters.group === "no_group") {
          if (groupId) return false;
        } else if (adminFilters.group !== "all") {
          if (groupId !== adminFilters.group) return false;
        }

        return true;
      });
    }
    return reportEntries.items;
  }, [
    activeTab,
    adminFilters.group,
    adminFilters.search,
    adminFilters.tipo,
    adminFilters.esperienza,
    reportEntries.items,
    volunteersMap,
  ]);

  const groupedReports = useMemo(() => {
    const items = filteredItems;
    const groups = items.reduce((acc, report) => {
      const dayKey = report.data_servizio || "n/d";
      if (!acc[dayKey]) {
        acc[dayKey] = [];
      }
      acc[dayKey].push(report);
      return acc;
    }, {});
    const sortedKeys = Object.keys(groups).sort((a, b) =>
      b.localeCompare(a)
    );
    const summary = items.reduce(
      (acc, report) => {
        const totalMinutes = (report.ore || 0) * 60 + (report.minuti || 0);
        acc.minutes += totalMinutes;
        acc.oreLdc += report.ore_ldc || 0;
        acc.studi += report.studi || 0;
        return acc;
      },
      { minutes: 0, oreLdc: 0, studi: 0 }
    );
    return { groups, sortedKeys, summary };
  }, [filteredItems]);

  const adminSummaries = useMemo(() => {
    if (!allowAdminScope || activeTab !== "admin") {
      return [];
    }
    const summaryMap = new Map();
    filteredItems.forEach((report) => {
      const volunteerKey =
        report.volontario_id != null
          ? String(report.volontario_id)
          : `anon-${report.volontario_nome || ""}-${report.volontario_cognome || ""}`;
      if (!summaryMap.has(volunteerKey)) {
        const volunteer =
          report.volontario_id != null
            ? volunteersMap.get(String(report.volontario_id))
            : null;
        const label =
          volunteer && (volunteer.nome || volunteer.cognome)
            ? `${volunteer.nome || ""} ${volunteer.cognome || ""}`.trim()
            : `${report.volontario_nome || ""} ${
                report.volontario_cognome || ""
              }`.trim() || "Volontario";
        const groupId = volunteer?.group_id
          ? String(volunteer.group_id)
          : null;
        const groupName = groupId ? groupsMap.get(groupId)?.nome || null : null;
        summaryMap.set(volunteerKey, {
          id: volunteerKey,
          volunteerId: report.volontario_id ?? null,
          label,
          groupId,
          groupName,
          totals: { minutes: 0, oreLdc: 0, studi: 0, pubblicazioni: 0 },
          publicationBreakdown: [],
          reportCount: 0,
        });
      }
      const entry = summaryMap.get(volunteerKey);
      entry.reportCount += 1;
      entry.totals.minutes += (report.ore || 0) * 60 + (report.minuti || 0);
      entry.totals.oreLdc += report.ore_ldc || 0;
      entry.totals.studi += report.studi || 0;
      const pubs = parsePublications(report.pubblicazioni);
      const copies = pubs.reduce(
        (acc, pub) => acc + (Number(pub?.copie) || 0),
        0
      );
      entry.totals.pubblicazioni += copies;
      pubs.forEach((pub) => {
        const pubCopies = Number(pub?.copie) || 0;
        if (!pubCopies) return;
        const title =
          (pub?.titolo && String(pub.titolo).trim()) ||
          "Pubblicazione";
        const language = (pub?.lingua && String(pub.lingua).trim()) || "";
        const key = `${title.toLowerCase()}|${language.toLowerCase()}`;
        let detail = entry.publicationBreakdown.find(
          (item) => item.key === key
        );
        if (!detail) {
          detail = { key, title, language, copies: 0 };
          entry.publicationBreakdown.push(detail);
        }
        detail.copies += pubCopies;
      });
    });
    const result = Array.from(summaryMap.values());
    result.forEach((item) => {
      item.publicationBreakdown.sort(
        (a, b) =>
          b.copies - a.copies ||
          a.title.localeCompare(b.title, "it", { sensitivity: "base" })
      );
    });
    return result.sort((a, b) =>
      a.label.localeCompare(b.label, "it", { sensitivity: "base" })
    );
  }, [
    activeTab,
    allowAdminScope,
    filteredItems,
    groupsMap,
    volunteersMap,
  ]);

  useEffect(() => {
    reportCacheRef.current = reportCache;
  }, [reportCache]);

  const postazioneOptions = useMemo(() => {
    if (!allowAdminScope) return [];
    return adminPostazioni.map((p) => ({
      id: String(p.id),
      name: p.luogo || "Postazione",
    }));
  }, [adminPostazioni, allowAdminScope]);

  const setCacheEntry = useCallback((scope, key, data) => {
    setReportCache((prev) => ({
      ...prev,
      [scope]: {
        ...prev[scope],
        [key]: {
          ...(prev[scope]?.[key] || {}),
          ...data,
        },
      },
    }));
  }, []);

  const loadReports = useCallback(
    async (scope, targetDate, options = {}) => {
      const key = formatMonthKey(targetDate);
      const currentCache = reportCacheRef.current;
      const existing = currentCache?.[scope]?.[key];
      if (
        existing &&
        existing.loaded &&
        !options.force
      ) {
        return;
      }

      setCacheEntry(scope, key, { loading: true, error: null });
      try {
        const params = new URLSearchParams({ mese: key });
        if (scope === "admin") {
          if (adminFilters.tipo !== "all") {
            params.append("tipo", adminFilters.tipo);
          }
          if (adminFilters.esperienza === "with") {
            params.append("esperienza", "true");
          } else if (adminFilters.esperienza === "without") {
            params.append("esperienza", "false");
          }
        }
        const endpoint =
          scope === "admin"
            ? "/rapporti-servizio/admin"
            : "/rapporti-servizio/miei";
        const response = await api.get(
          `${endpoint}?${params.toString()}`
        );
        const items = Array.isArray(response.data?.rapporti)
          ? response.data.rapporti
          : [];
        setCacheEntry(scope, key, {
          items,
          loading: false,
          loaded: true,
          error: null,
          range: response.data?.range || null,
        });
      } catch (error) {
        console.error("Errore caricamento rapporti:", error);
        toastError(
          error.response?.data?.message ||
            "Errore durante il caricamento dei rapporti"
        );
        setCacheEntry(scope, key, {
          loading: false,
          loaded: true,
          error:
            error.response?.data?.message ||
            "Errore durante il caricamento",
        });
      }
    },
    [adminFilters, setCacheEntry]
  );

  useEffect(() => {
    if (!rapportoEnabled) {
      return;
    }
    if (allowPersonalScope) {
      loadReports("miei", monthState.miei);
    }
    if (allowAdminScope) {
      loadReports("admin", monthState.admin);
    }
  }, [
    rapportoEnabled,
    allowPersonalScope,
    allowAdminScope,
    loadReports,
    monthState.miei,
    monthState.admin,
    activeCongregazione?.id,
  ]);

  useEffect(() => {
    if (allowAdminScope && rapportoEnabled) {
      loadReports("admin", monthState.admin, { force: true });
    }
  }, [
    adminFilters,
    allowAdminScope,
    rapportoEnabled,
    loadReports,
    monthState.admin,
  ]);

  useEffect(() => {
    if (!rapportoEnabled || !activeCongregazione?.id) {
      return;
    }
    const fetchPostazioni = async () => {
      setPostazioniLoading(true);
      try {
        const response = await api.get(
          `/postazioni?congregazione_id=${activeCongregazione.id}`
        );
        const list = Array.isArray(response.data) ? response.data : [];
        const filtered = list
          .filter((p) => (p.stato || "").toLowerCase() === "attiva")
          .map((p) => ({
            ...p,
            slot_orari: Array.isArray(p.slot_orari)
              ? p.slot_orari.filter(
                  (so) => (so.stato || "").toLowerCase() === "attivo"
                )
              : [],
          }));
        setPostazioni(filtered);
      } catch (error) {
        console.error("Errore caricamento postazioni:", error);
        toastError(
          error.response?.data?.message ||
            "Impossibile caricare le postazioni"
        );
      } finally {
        setPostazioniLoading(false);
      }
    };
    fetchPostazioni();
  }, [rapportoEnabled, activeCongregazione?.id]);

  const loadAdminSupportData = useCallback(async () => {
    if (!allowAdminScope || !rapportoEnabled || !activeCongregazione?.id) {
      return;
    }
    setSupportingDataLoading(true);
    try {
      const [groupsRes, volunteersRes, postazioniRes] = await Promise.all([
        api.get("/gruppi-servizio"),
        api.get("/volontari", { params: { limit: 1000 } }),
        api.get("/postazioni", { params: { stato: "attiva" } }),
      ]);
      const fetchedGroups = groupsRes.data?.gruppi || groupsRes.data || [];
      const fetchedVolontari =
        volunteersRes.data?.volontari ||
        volunteersRes.data?.items ||
        volunteersRes.data ||
        [];
      const fetchedPostazioni = Array.isArray(postazioniRes.data)
        ? postazioniRes.data
        : [];
      setServiceGroups(Array.isArray(fetchedGroups) ? fetchedGroups : []);
      setVolontari(Array.isArray(fetchedVolontari) ? fetchedVolontari : []);
      setAdminPostazioni(fetchedPostazioni);
    } catch (error) {
      console.error("Errore caricamento gruppi o volontari:", error);
      toastError(
        error.response?.data?.message ||
          "Impossibile caricare i gruppi di servizio"
      );
    } finally {
      setSupportingDataLoading(false);
    }
  }, [activeCongregazione?.id, allowAdminScope, rapportoEnabled]);

  useEffect(() => {
    if (allowAdminScope && rapportoEnabled && activeCongregazione?.id) {
      loadAdminSupportData();
    }
  }, [
    activeCongregazione?.id,
    allowAdminScope,
    rapportoEnabled,
    loadAdminSupportData,
  ]);

  const changeMonth = (scope, direction) => {
    setMonthState((prev) => {
      const current = prev[scope];
      const next = startOfMonth(
        new Date(current.getFullYear(), current.getMonth() + direction, 1)
      );
      if (direction > 0) {
        const today = startOfMonth(new Date());
        if (next > today) {
          return prev;
        }
      }
      loadReports(scope, next);
      return { ...prev, [scope]: next };
    });
  };

  const resetFormState = () => {
    setFormData(createInitialFormState());
    setExperienceEnabled(false);
    setExperienceNote("");
  };

  const openModal = (scope, report = null) => {
    if (report) {
      const publications = parsePublications(report.pubblicazioni);
      setFormData({
        tipo:
          isServizioSpeciale && report.tipo !== "postazione"
            ? "postazione"
            : report.tipo || "postazione",
        data_servizio: report.data_servizio || "",
        postazione_id: report.postazione_id || "",
        slot_orario_id: report.slot_orario_id || "",
        ore: report.ore || 0,
        minuti: report.minuti || 0,
        ore_ldc: report.ore_ldc || 0,
        studi: report.studi || 0,
        pubblicazioni: publications,
        note: report.note || "",
        esperienza_id: report.esperienza_id || null,
      });
      setExperienceEnabled(false);
      setExperienceNote("");
    } else {
      resetFormState();
      if (isServizioSpeciale) {
        setFormData((prev) => ({ ...prev, tipo: "postazione" }));
      }
    }
    setModalState({ open: true, scope, report });
  };

  const closeModal = () => {
    setModalState({ open: false, scope: "miei", report: null });
    resetFormState();
  };

  const handleFormChange = (field, value) => {
    if (field === "tipo" && isServizioSpeciale) {
      // In servizio speciale il tipo è sempre postazione
      return;
    }
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "tipo" && value === "postazione") {
        next.ore_ldc = 0;
      }
      return next;
    });
  };

  const handlePublicationChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = prev.pubblicazioni ? [...prev.pubblicazioni] : [];
      updated[index] = {
        ...updated[index],
        [field]: field === "copie" ? normalizePublicationQuantityInput(value) : value,
      };
      return { ...prev, pubblicazioni: updated };
    });
  };

  const addPublicationRow = () => {
    setFormData((prev) => ({
      ...prev,
      pubblicazioni: [
        ...(prev.pubblicazioni || []),
        { lingua: "Italiano", titolo: "Bibbia", copie: "" },
      ],
    }));
  };

  const removePublicationRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      pubblicazioni: prev.pubblicazioni.filter((_item, idx) => idx !== index),
    }));
  };

  const getSlotOptions = (postazioneId) => {
    const selected = postazioni.find(
      (postazione) => String(postazione.id) === String(postazioneId)
    );
    return selected?.slot_orari || [];
  };

  const minDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - MAX_REPORT_WINDOW_DAYS);
    return date.toISOString().slice(0, 10);
  }, []);

  const maxDate = useMemo(
    () => new Date().toISOString().slice(0, 10),
    []
  );

  const refreshCurrentMonth = (scope) => {
    loadReports(scope, monthState[scope], { force: true });
  };

  const handleDelete = async (report, scope) => {
    const confirmDelete = window.confirm(
      "Vuoi davvero eliminare questo rapporto?"
    );
    if (!confirmDelete) {
      return;
    }
    try {
      const endpoint =
        scope === "admin"
          ? `/rapporti-servizio/admin/${report.id}`
          : `/rapporti-servizio/miei/${report.id}`;
      await api.delete(endpoint);
      toastSuccess("Rapporto eliminato correttamente.");
      refreshCurrentMonth(scope);
    } catch (error) {
      console.error("Errore eliminazione rapporto:", error);
      toastError(
        error.response?.data?.message ||
          "Errore durante l'eliminazione del rapporto"
      );
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (formData.tipo === "postazione") {
      if (!formData.postazione_id) {
        toastError("Seleziona una postazione.");
        return;
      }
      if (!formData.slot_orario_id) {
        toastError("Seleziona uno slot orario.");
        return;
      }
    }

    if (experienceEnabled && experienceNote.trim().length < 10) {
      toastError(
        "Per aggiungere l'esperienza inserisci almeno 10 caratteri."
      );
      return;
    }

    const invalidPublicationIndex = (formData.pubblicazioni || []).findIndex(
      (item) => !isValidPublicationQuantity(item.copie)
    );
    if (invalidPublicationIndex !== -1) {
      toastError("Inserisci un numero di copie maggiore di zero per ogni pubblicazione.");
      publicationQuantityInputRefs.current[invalidPublicationIndex]?.focus();
      return;
    }

    setSubmitting(true);
    let createdExperienceId = null;
    try {
      if (experienceEnabled) {
        const esperienzaPayload = {
          postazione_id:
            formData.tipo === "postazione"
              ? Number(formData.postazione_id)
              : null,
          slot_orario_id:
            formData.tipo === "postazione"
              ? Number(formData.slot_orario_id)
              : null,
          data: formData.data_servizio,
          racconto: experienceNote.trim(),
        };
        const esperienzaResponse = await api.post(
          "/esperienze",
          esperienzaPayload
        );
        createdExperienceId = esperienzaResponse.data?.id;
      }

      const payload = {
        ...formData,
        postazione_id:
          formData.tipo === "postazione" && formData.postazione_id
            ? Number(formData.postazione_id)
            : null,
        slot_orario_id:
          formData.tipo === "postazione" && formData.slot_orario_id
            ? Number(formData.slot_orario_id)
            : null,
        ore: Number(formData.ore) || 0,
        minuti: Number(formData.minuti) || 0,
        ore_ldc: Number(formData.ore_ldc) || 0,
        studi: Number(formData.studi) || 0,
        pubblicazioni: (formData.pubblicazioni || []).map((item) => ({
          lingua: item.lingua || "Italiano",
          titolo: item.titolo || "",
          copie: parsePublicationQuantity(item.copie),
        })),
        esperienza_id: createdExperienceId || formData.esperienza_id || null,
      };

      if (modalState.report) {
        const endpoint =
          modalState.scope === "admin"
            ? `/rapporti-servizio/admin/${modalState.report.id}`
            : `/rapporti-servizio/miei/${modalState.report.id}`;
        await api.put(endpoint, payload);
        toastSuccess("Rapporto aggiornato con successo.");
      } else {
        await api.post("/rapporti-servizio", payload);
        toastSuccess("Rapporto salvato con successo.");
      }

      refreshCurrentMonth(modalState.scope);
      if (
        modalState.scope === "admin" &&
        activeTab === "admin"
      ) {
        refreshCurrentMonth("admin");
      } else {
        refreshCurrentMonth("miei");
      }
      closeModal();
    } catch (error) {
      console.error("Errore salvataggio rapporto:", error);
      if (createdExperienceId) {
        try {
          await api.delete(`/esperienze/${createdExperienceId}`);
        } catch (_error) {
          // ignore cleanup error
        }
      }
      toastError(
        error.response?.data?.message ||
          "Errore durante il salvataggio del rapporto"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!allowPersonalScope && !allowAdminScope) {
    return null;
  }

  if (!rapportoEnabled) {
    return null;
  }

  const currentMonthDate = monthState[activeTab];
  const showTabs = allowPersonalScope && allowAdminScope;
  const isAdminView = activeTab === "admin";
  const isNextDisabled =
    currentMonthDate >= startOfMonth(new Date());

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 mt-6 w-full min-w-0">
      {showHeader ? (
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ChartBarIcon className="h-6 w-6 text-primary-600" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Rapporto di Servizio
              </h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Organizza ore, pubblicazioni e esperienze in modo ordinato, mese
              per mese.
            </p>
          </div>
          <button
            onClick={() => openModal(activeTab)}
            className="inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nuovo rapporto
          </button>
        </div>
      ) : null}
      {showHeader ? (
        <div className="accent-line mt-4" />
      ) : null}

      {showTabs && (
        <div className="mt-6 flex border rounded-lg overflow-hidden">
          {allowPersonalScope && (
            <button
              className={`flex-1 px-4 py-2 text-sm font-medium ${
                activeTab === "miei"
                  ? "bg-primary-600 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
              onClick={() => setActiveTab("miei")}
            >
              I miei rapporti
            </button>
          )}
          {allowAdminScope && (
            <button
              className={`flex-1 px-4 py-2 text-sm font-medium ${
                activeTab === "admin"
                  ? "bg-primary-600 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
              onClick={() => {
                setActiveTab("admin");
                loadReports("admin", monthState.admin);
              }}
            >
              Rapporti congregazione
            </button>
          )}
        </div>
      )}

      {isAdminView && allowAdminScope && (
        <div className="mt-6 flex flex-wrap gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase">
              Tipo rapporto
            </label>
            <select
              value={adminFilters.tipo}
              onChange={(e) =>
                setAdminFilters((prev) => ({
                  ...prev,
                  tipo: e.target.value,
                }))
              }
              className="mt-1 border rounded-md px-3 py-2 text-sm"
            >
              <option value="all">Tutti</option>
              <option value="postazione_all">Postazioni (tutte)</option>
              <option value="normale">Rapporti normali</option>
              {postazioneOptions.length > 0 && (
                <optgroup label="Postazioni">
                  {postazioneOptions.map((p) => (
                    <option
                      key={p.id}
                      value={`postazione_${p.id}`}
                    >
                      {p.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase">
              Esperienze
            </label>
            <select
              value={adminFilters.esperienza}
              onChange={(e) =>
                setAdminFilters((prev) => ({
                  ...prev,
                  esperienza: e.target.value,
                }))
              }
              className="mt-1 border rounded-md px-3 py-2 text-sm"
            >
              <option value="all">Tutte</option>
              <option value="with">Solo con esperienza</option>
              <option value="without">Solo senza esperienza</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase">
              Gruppo di servizio
            </label>
            <select
              value={adminFilters.group}
              onChange={(e) =>
                setAdminFilters((prev) => ({
                  ...prev,
                  group: e.target.value,
                }))
              }
              className="mt-1 border rounded-md px-3 py-2 text-sm"
              disabled={supportingDataLoading}
            >
              <option value="all">Tutti</option>
              <option value="no_group">Senza gruppo</option>
              {serviceGroups.map((group) => (
                <option key={group.id} value={String(group.id)}>
                  {group.nome}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-semibold text-gray-700 uppercase">
              Cerca proclamatori
            </label>
            <input
              type="text"
              value={adminFilters.search}
              onChange={(e) =>
                setAdminFilters((prev) => ({
                  ...prev,
                  search: e.target.value,
                }))
              }
              placeholder="Nome o cognome"
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
            />
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between border rounded-lg px-4 py-2 bg-gray-50">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <CalendarIcon className="h-5 w-5 text-primary-600" />
          <span className="capitalize">
            {formatMonthLabel(monthState[activeTab])}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => changeMonth(activeTab, -1)}
            className="p-2 rounded-full border border-gray-200 hover:bg-gray-100"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => changeMonth(activeTab, 1)}
            className="p-2 rounded-full border border-gray-200 hover:bg-gray-100 disabled:opacity-50"
            disabled={isNextDisabled}
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="mt-6">
        {reportEntries.loading ? (
          <div className="text-center py-8 text-gray-500">
            Caricamento rapporti in corso...
          </div>
        ) : reportEntries.error ? (
          <div className="text-center py-8">
            <p className="text-sm text-red-600 mb-3">
              {reportEntries.error}
            </p>
            <button
              onClick={() => refreshCurrentMonth(activeTab)}
              className="inline-flex items-center px-4 py-2 border border-primary-600 text-primary-600 rounded-md hover:bg-primary-50 transition"
            >
              Riprova
            </button>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Nessun rapporto per il mese selezionato.
          </div>
        ) : isAdminView && allowAdminScope ? (
          <div className="space-y-4" ref={adminSummaryRef}>
            {adminSummaries.map((summary) => (
              <div
                key={summary.id}
                className="relative border rounded-lg p-4 pl-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
              >
                <span
                  className="accent-bar top-3 bottom-3"
                  aria-hidden="true"
                />
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {summary.label}
                  </p>
                  <p className="text-xs text-gray-500">
                    {summary.groupName || "Nessun gruppo"}
                  </p>
                  <p className="text-[11px] text-gray-400">
                    {summary.reportCount}{" "}
                    {summary.reportCount === 1 ? "rapporto" : "rapporti"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-gray-700">
                  <span className="inline-flex items-center rounded-full bg-gray-50 border px-3 py-1 font-medium">
                    Ore: {formatDuration(summary.totals.minutes)}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-gray-50 border px-3 py-1 font-medium">
                    Ore LDC: {summary.totals.oreLdc || 0}
                  </span>
                  <span className="inline-flex items-center rounded-full bg-gray-50 border px-3 py-1 font-medium">
                    Studi: {summary.totals.studi || 0}
                  </span>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setOpenPublicationSummaryId((prev) =>
                          prev === summary.id ? null : summary.id
                        )
                      }
                      className="inline-flex items-center rounded-full bg-gray-50 border px-3 py-1 font-medium hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
                    >
                      Pubblicazioni: {summary.totals.pubblicazioni || 0}
                    </button>
                    {openPublicationSummaryId === summary.id && (
                      <div className="absolute right-0 mt-2 w-72 z-20 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                        <p className="text-[11px] font-semibold text-gray-700 mb-2">
                          Dettaglio pubblicazioni
                        </p>
                        {summary.publicationBreakdown &&
                        summary.publicationBreakdown.length ? (
                          <ul className="space-y-1 max-h-56 overflow-auto text-xs text-gray-700">
                            {summary.publicationBreakdown.map((pub) => (
                              <li
                                key={`${summary.id}-${pub.key}`}
                                className="flex items-center justify-between gap-2"
                              >
                                <span className="truncate">
                                  {pub.title}
                                  {pub.language
                                    ? ` (${pub.language})`
                                    : ""}
                                </span>
                                <span className="font-semibold">
                                  {pub.copies}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-gray-500">
                            Nessuna pubblicazione registrata
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {groupedReports.sortedKeys.map((dayKey) => (
              <div key={dayKey} className="border rounded-lg">
                <div className="px-4 py-2 border-b bg-gray-50 flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-800 capitalize">
                    {formatDateLabel(dayKey)}
                  </h4>
                </div>
                <div className="divide-y">
                  {(groupedReports.groups[dayKey] || []).map((report) => {
                    const typeLabel =
                      report.tipo === "postazione"
                        ? `Rapporto Postazione: ${
                            report.postazione_nome || "Postazione"
                          }`
                        : "Rapporto normale";
                    const serviceDateLabel = formatDateLabel(
                      report.data_servizio || dayKey
                    );
                    return (
                      <div
                        key={report.id}
                        className="relative p-4 pl-6 flex flex-col gap-3 text-sm text-gray-700"
                      >
                        <span
                          className="accent-bar top-3 bottom-3"
                          aria-hidden="true"
                        />
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium text-gray-900">
                            {typeLabel}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openModal(activeTab, report)}
                              className="p-1 rounded-full border text-gray-500 hover:text-primary-600"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(report, activeTab)}
                              className="p-1 rounded-full border text-gray-500 hover:text-red-600"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500">{serviceDateLabel}</p>
                    <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                      <div>
                        <span className="font-semibold text-gray-700">
                          Ore
                        </span>
                        : {report.ore || 0}h {report.minuti || 0}m
                      </div>
                      {report.tipo !== "postazione" && (
                        <div>
                          <span className="font-semibold text-gray-700">
                            Ore LDC
                          </span>
                          : {report.ore_ldc || 0}
                        </div>
                      )}
                      <div>
                        <span className="font-semibold text-gray-700">
                          Studi
                        </span>
                        : {report.studi || 0}
                          </div>
                          {report.esperienza_id && (
                            <span className="inline-flex items-center rounded-full bg-primary-50 text-primary-700 px-2 py-0.5 text-[11px]">
                              Esperienza registrata
                            </span>
                          )}
                        </div>
                        {report.pubblicazioni &&
                          parsePublications(report.pubblicazioni).length >
                            0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-700">
                                Pubblicazioni
                              </p>
                              <ul className="text-xs text-gray-600 list-disc list-inside">
                                {parsePublications(
                                  report.pubblicazioni
                                ).map((pub, idx) => (
                                  <li key={idx}>
                                    {pub.copie} copie - {pub.titolo || "—"} (
                                    {pub.lingua || "Lingua"})
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        {report.note && (
                          <p className="text-xs italic text-gray-500">
                            “{report.note}”
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalState.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-start justify-center overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl mt-10 max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {modalState.report ? "Modifica rapporto" : "Nuovo rapporto"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isServizioSpeciale ? (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase">
                      Tipo
                    </label>
                    <div className="mt-1 flex gap-3">
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="tipo"
                          value="postazione"
                          checked={formData.tipo === "postazione"}
                          onChange={() =>
                            handleFormChange("tipo", "postazione")
                          }
                        />
                        <span className="ml-2 text-sm">Postazione</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input
                          type="radio"
                          name="tipo"
                          value="normale"
                          checked={formData.tipo === "normale"}
                          onChange={() =>
                            handleFormChange("tipo", "normale")
                          }
                        />
                        <span className="ml-2 text-sm">Servizio libero (casa in casa / altro)</span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase">
                      Tipo
                    </label>
                    <p className="mt-2 text-sm font-medium text-gray-800">
                      Rapporto Postazione (servizio speciale)
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase">
                    Giorno
                  </label>
                  <input
                    type="date"
                    value={formData.data_servizio}
                    onChange={(e) =>
                      handleFormChange("data_servizio", e.target.value)
                    }
                    min={minDate}
                    max={maxDate}
                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                    required
                  />
                </div>
              </div>

              {formData.tipo === "postazione" && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase">
                        Postazione
                      </label>
                      <select
                        value={formData.postazione_id}
                        onChange={(e) => {
                          handleFormChange("postazione_id", e.target.value);
                          handleFormChange("slot_orario_id", "");
                        }}
                        className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                        disabled={postazioniLoading}
                      >
                        <option value="">Seleziona</option>
                        {visiblePostazioni.map((postazione) => (
                          <option key={postazione.id} value={postazione.id}>
                            {postazione.luogo}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase">
                        Slot orario
                      </label>
                      <select
                        value={formData.slot_orario_id}
                        onChange={(e) =>
                          handleFormChange("slot_orario_id", e.target.value)
                        }
                        className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                      >
                        <option value="">Seleziona</option>
                        {getSlotOptions(formData.postazione_id).map((slot) => (
                          <option key={slot.id} value={slot.id}>
                            {slot.orario_inizio?.slice(0, 5)} -{" "}
                            {slot.orario_fine?.slice(0, 5)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div className="flex flex-col md:flex-row md:items-end gap-3 md:col-span-2">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-700 uppercase">
                      Ore di servizio
                    </label>
                    <div className="mt-1 flex gap-2 items-center">
                      <button
                        type="button"
                        className="px-2 py-1 border rounded-md"
                        onClick={() =>
                          handleFormChange(
                            "ore",
                            Math.max(0, (formData.ore || 0) - 1)
                          )
                        }
                      >
                        −
                      </button>
                      <span className="w-12 text-center text-sm">
                        {formData.ore || 0}
                      </span>
                      <button
                        type="button"
                        className="px-2 py-1 border rounded-md"
                        onClick={() =>
                          handleFormChange(
                            "ore",
                            Math.min(24, (formData.ore || 0) + 1)
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-700 uppercase">
                      Minuti
                    </label>
                    <div className="mt-1 flex gap-2 items-center">
                      <button
                        type="button"
                        className="px-2 py-1 border rounded-md"
                        onClick={() =>
                          handleFormChange(
                            "minuti",
                            ((formData.minuti || 0) - 15 + 60) % 60
                          )
                        }
                      >
                        −
                      </button>
                      <span className="w-12 text-center text-sm">
                        {formData.minuti || 0}
                      </span>
                      <button
                        type="button"
                        className="px-2 py-1 border rounded-md"
                        onClick={() =>
                          handleFormChange(
                            "minuti",
                            ((formData.minuti || 0) + 15) % 60
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                {formData.tipo !== "postazione" && (
                  <div className="md:col-span-1">
                    <label className="block text-xs font-semibold text-gray-700 uppercase">
                      Ore LDC
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="24"
                      value={formData.ore_ldc}
                      onChange={(e) =>
                        handleFormChange("ore_ldc", Number(e.target.value))
                      }
                      className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                )}
                <div className="md:col-span-1">
                  <label className="block text-xs font-semibold text-gray-700 uppercase">
                    Studi
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.studi}
                    onChange={(e) =>
                      handleFormChange("studi", Number(e.target.value))
                    }
                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase">
                    Pubblicazioni
                  </h4>
                  <button
                    type="button"
                    onClick={addPublicationRow}
                    className="min-h-11 px-1 text-sm text-primary-600 hover:text-primary-700"
                  >
                    + Aggiungi pubblicazione
                  </button>
                </div>
                {(formData.pubblicazioni || []).length === 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Nessuna pubblicazione aggiunta.
                  </p>
                )}
                <div className="mt-3 space-y-3">
                  {(formData.pubblicazioni || []).map((pub, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm sm:p-4 md:flex-row md:items-end"
                    >
                      <label className="block w-full text-xs font-semibold text-gray-700 md:w-52 md:shrink-0">
                        Lingua
                        <select
                          value={pub.lingua || "Italiano"}
                          onChange={(e) =>
                            handlePublicationChange(
                              index,
                              "lingua",
                              e.target.value
                            )
                          }
                          className="mt-1 min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-900"
                        >
                          {PUBLICATION_LANGUAGES.map((lang) => (
                            <option key={lang} value={lang}>
                              {lang}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="w-full min-w-0 flex-1">
                        <label className="block text-xs font-semibold text-gray-700">
                          Pubblicazione
                          <select
                            value={
                              PUBLICATION_TITLES.includes(pub.titolo)
                                ? pub.titolo
                                : "Altro"
                            }
                            onChange={(e) => {
                              const selected = e.target.value;
                              if (selected === "Altro") {
                                const current =
                                  PUBLICATION_TITLES.includes(pub.titolo) ||
                                  !pub.titolo
                                    ? ""
                                    : pub.titolo;
                                handlePublicationChange(
                                  index,
                                  "titolo",
                                  current
                                );
                              } else {
                                handlePublicationChange(
                                  index,
                                  "titolo",
                                  selected
                                );
                              }
                            }}
                            className="mt-1 min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base text-gray-900"
                          >
                            {PUBLICATION_TITLES.map((title) => (
                              <option key={title} value={title}>
                                {title === "Altro"
                                  ? "Altro (campo di inserimento libero)"
                                  : title}
                              </option>
                            ))}
                          </select>
                        </label>
                        {(!PUBLICATION_TITLES.includes(pub.titolo) ||
                          pub.titolo === "Altro") && (
                          <input
                            type="text"
                            value={
                              PUBLICATION_TITLES.includes(pub.titolo)
                                ? ""
                                : pub.titolo || ""
                            }
                            onChange={(e) =>
                              handlePublicationChange(
                                index,
                                "titolo",
                                e.target.value
                              )
                            }
                            placeholder="Inserisci titolo"
                            className="mt-2 min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base"
                          />
                        )}
                      </div>
                      <label className="block w-full text-xs font-semibold text-gray-700 sm:w-36 md:w-28 md:shrink-0">
                        Copie
                        <input
                          ref={(element) => {
                            publicationQuantityInputRefs.current[index] = element;
                          }}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          autoComplete="off"
                          value={pub.copie ?? ""}
                          onChange={(e) =>
                            handlePublicationChange(
                              index,
                              "copie",
                              e.target.value
                            )
                          }
                          onFocus={(event) => event.currentTarget.select()}
                          placeholder="Es. 5"
                          aria-label={`Numero copie per ${pub.titolo || "pubblicazione"}`}
                          className="mt-1 min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base"
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => removePublicationRow(index)}
                        className="inline-flex min-h-11 items-center justify-center rounded-md border border-red-100 bg-white px-3 text-sm font-medium text-red-600 hover:bg-red-50"
                      >
                        Rimuovi
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase">
                  Note
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => handleFormChange("note", e.target.value)}
                  rows={2}
                  className="mt-1 w-full border rounded-md px-3 py-2 text-sm"
                  placeholder="Aggiungi informazioni utili"
                />
              </div>

              <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">
                      Esperienza collegata
                    </h4>
                    {modalState.report?.esperienza_id && (
                      <p className="text-xs text-gray-500">
                        Questo rapporto ha già un'esperienza registrata. Usa la
                        sezione “Esperienze” per modificarla.
                      </p>
                    )}
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={
                        modalState.report?.esperienza_id
                          ? false
                          : experienceEnabled
                      }
                      disabled={
                        Boolean(modalState.report?.esperienza_id)
                      }
                      onChange={(e) =>
                        setExperienceEnabled(e.target.checked)
                      }
                    />
                    Aggiungi esperienza
                  </label>
                </div>
                {experienceEnabled && (
                  <textarea
                    value={experienceNote}
                    onChange={(e) => setExperienceNote(e.target.value)}
                    rows={4}
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    placeholder="Racconta l'esperienza (minimo 10 caratteri)"
                  />
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border rounded-md text-sm text-gray-600"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm hover:bg-primary-700 disabled:opacity-50"
                >
                  {submitting ? "Salvataggio..." : "Salva rapporto"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RapportiServizioSection;
